import PermissionButton from 'components/buttons/PermissionButton';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useLazyGetTasksAddressedToEmployeeQuery } from 'store/api/tasks';
import { AddressedTaskListItem } from 'types/tasks';
import { useClassifiers } from 'utils/useClassifiers';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import AddressedTaskItem from './AddressedTaskItem';

interface AddressedTaskListProps {
    tasks: AddressedTaskListItem[];
    userId: number | null;
    openAddressedTaskView: (taskId: number) => void;
    toggleDrawer: () => void;
    filterTasks?: (task: AddressedTaskListItem) => boolean;
}

interface AddressedTaskSearchForm {
    searchLine: string;
}

const AddressedTasksList: React.FC<AddressedTaskListProps> = ({
    tasks,
    userId,
    openAddressedTaskView,
    toggleDrawer,
    filterTasks,
}) => {
    const navigate = useNavigateWithReturnState();
    const [searchTasks, { data: addressedTasks }] = useLazyGetTasksAddressedToEmployeeQuery();
    const { classifiers } = useClassifiers({
        codes: ['TASK_PRIORITY_TYPE', 'TASK_TYPE'],
    });

    const { control, getValues } = useForm<AddressedTaskSearchForm>({
        defaultValues: {
            searchLine: '',
        },
    });

    const onSearchSubmit = async (value: string): Promise<void> => {
        if (userId === null) {
            return;
        }

        const trimmedSearch = value.trim();
        searchTasks({ employeeId: userId, search: trimmedSearch });
    };

    const handleSearchChange = useDebouncedCallback(() => {
        onSearchSubmit(getValues('searchLine'));
    }, 600);

    const handleAddNewTas = () => {
        toggleDrawer();
        navigate('task/new');
    };

    const filteredTasks = useMemo(() => {
        const tasksToFilter = addressedTasks ?? tasks;
        if (!filterTasks) {
            return tasksToFilter;
        }
        return tasksToFilter.filter(filterTasks);
    }, [addressedTasks, tasks, filterTasks]);

    return (
        <>
            <div className="border-divider flex w-full items-end justify-around border-b px-6 pt-2 pb-4">
                <TextInput
                    wrapperClassName="h-full w-3/4"
                    control={control}
                    name="searchLine"
                    placeholder="search"
                    onChange={handleSearchChange}
                />
                <PermissionButton
                    className="h-9"
                    permissions={['CREATE.TASKS']}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleAddNewTas}
                >
                    <FormattedMessage id="tasks.add-task" />
                </PermissionButton>
            </div>
            <ul>
                {filteredTasks.map((val) => (
                    <AddressedTaskItem
                        key={`addressed-task-${val.id}`}
                        addressedTask={val}
                        openAddressedTaskView={openAddressedTaskView}
                        classifiers={classifiers}
                    />
                ))}
            </ul>
        </>
    );
};

export default AddressedTasksList;
